
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import {
  persistOnboardingCompleted,
  resolveUserSettingsContext,
  fetchSetupStatus,
  recordSetupShown,
  recordSetupDeclined,
} from '@/lib/userSettingsApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, ArrowRight, ArrowLeft, ExternalLink, AlertTriangle, Circle } from 'lucide-react';
import { useToast } from '@/components/use-toast';
import AnimatedNeoSymbol from './AnimatedNeoSymbol';

const STEPS = [
  {
    type: 'modal',
    title: "Hi, I'm NEO GTM — your go-to-market co-pilot",
    content: "This platform helps you cut through the noise to win faster by highlighting top accounts, key people, and the plays that move deals forward.\nThink of it as your partner in the cockpit — keeping your eyes on the right targets.",
    primaryButton: "Let's Start",
    secondaryButton: "Skip for now"
  },
  {
    type: 'simple',
    title: "Your Top Accounts",
    content: "The Top Accounts list shows you which accounts have the best timing and fit, so you know where to spend your time today."
  },
  {
    type: 'simple', 
    title: "Procurement",
    content: "Procurement flags open tenders, upcoming renewals, and opportunities to displace incumbents. When timing is critical, you won't miss it."
  },
  {
    type: 'simple',
    title: "Top Personas",
    content: "Deals move with people. This section surfaces the champions and decision-makers inside your accounts, so you know exactly who to engage with."
  },
  {
    type: 'simple',
    title: "NEO Assistant",
    content: "You can also chat directly with NEO. Ask for your next steps, tailored outreach, or fresh signals. It's designed to keep things simple and actionable."
  },
  /* Filled in at runtime from the rep's ACTUAL configuration, and dropped entirely when there is
   * nothing missing — see SETUP_STEP below. */
  { type: 'setup', title: 'Two minutes of setup' },
  {
    type: 'modal',
    title: "You're ready to fly",
    content: "You've covered the basics.\nLet's get started. If you ever get stuck, just ask.",
    primaryButton: "Let's go",
    quickActions: ["Meeting Prep"]
  }
];

/* A friendlier word than the service's key, which is a database label. */
const SETUP_LABELS = {
  mailbox: 'Connect your mailbox',
  brand_voice: 'Set your brand voice',
  hooks: 'Add your hooks',
  signature: 'Add an email signature',
  product_profile: 'Describe what you sell',
};

export default function OnboardingTour({ onComplete, onQuickAction }) {
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const { toast } = useToast();

  /* ── what this rep still has to configure ──────────────────────────────────────────────────────
   *
   * Tiago: "if they are empty, it should be flagged... to be able to send out emails in the system,
   * they need to authenticate in the platform (share the link) via gmail or outlook... these
   * recommendations on configuration should appear in the onboarding."
   *
   * 🔴 READ FROM THE SERVER, NEVER DERIVED HERE. The chatbot and the Claude connector answer the
   * same question from one service. A second derivation in this component is how a welcome screen
   * ends up congratulating a rep on a mailbox the assistant is simultaneously telling them to
   * connect.
   *
   * ⚠️ null MEANS UNKNOWN, NOT FINE. A failed read drops the step rather than claiming the rep is
   * set up — a tour that says "you're all set" to someone who cannot send anything is worse than
   * one that says nothing. */
  const [setup, setSetup] = useState(null);
  const [setupLoaded, setSetupLoaded] = useState(false);
  const shownRecordedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const { userId, tenantId } = resolveUserSettingsContext(contextUser, contextTenant);
    if (!userId || !tenantId) { setSetupLoaded(true); return undefined; }
    /* 🔴 BOUNDED. The tour waits for this read (see the render gate below), so a request that
     * never settles would leave a new rep staring at nothing on their first visit. Two and a half
     * seconds, then carry on without the step — losing a checklist is recoverable, losing the
     * whole welcome is not. */
    const timer = setTimeout(() => { if (!cancelled) setSetupLoaded(true); }, 2500);
    fetchSetupStatus(userId, tenantId).then((st) => {
      if (cancelled) return;
      clearTimeout(timer);
      setSetup(st);
      setSetupLoaded(true);
    });
    return () => { cancelled = true; clearTimeout(timer); };
  }, [contextUser, contextTenant]);

  /* The onboarding screen is the one place entitled to show `missing` in full rather than `raise`:
   * the rep is here on purpose, so nothing is being pushed at them. */
  const missing = useMemo(() => (Array.isArray(setup?.missing) ? setup.missing : []), [setup]);

  /* 🔴 A CHECKLIST WITH NOTHING ON IT IS NOISE. The step is dropped for a rep who is already
   * configured, rather than shown as a page of ticks, so the dots and the Next button stay honest
   * about how long the tour is. It is also dropped until the read resolves, so the step count
   * cannot change under the rep mid-tour. */
  const steps = useMemo(
    () => STEPS.filter((st) => st.type !== 'setup' || (setupLoaded && missing.length > 0)),
    [setupLoaded, missing.length],
  );

  const currentStepData = steps[Math.min(currentStep, steps.length - 1)];

  /* The cooldown starts when the item is actually in front of them — not on mount of a tour whose
   * setup step may never be reached. */
  useEffect(() => {
    if (currentStepData?.type !== 'setup' || shownRecordedRef.current || missing.length === 0) return;
    shownRecordedRef.current = true;
    const { userId, tenantId } = resolveUserSettingsContext(contextUser, contextTenant);
    recordSetupShown(userId, tenantId, missing.map((m) => m.key));
  }, [currentStepData, missing, contextUser, contextTenant]);

  /* "If they otherwise say they wont do it, dont ask them." Permanent, and only from an explicit
   * click — never from Skip, which is a deferral and comes back in a week. */
  const handleDeclineSetup = useCallback(async () => {
    const { userId, tenantId } = resolveUserSettingsContext(contextUser, contextTenant);
    await recordSetupDeclined(userId, tenantId, missing.map((m) => m.key));
    toast({
      title: "Got it — we won't bring this up again",
      description: 'You can still set it up any time from Settings.',
      duration: 6000,
    });
    setCurrentStep((n) => Math.min(n + 1, steps.length - 1));
  }, [contextUser, contextTenant, missing, steps.length, toast]);

  const markComplete = useCallback(async () => {
    const userKey = contextUser?.id || contextUser?.email || 'anon';
    const COMPLETED_KEY = `neo-onboarding-completed:${userKey}`;
    const NEW_SIGNUP_KEY = `new-signup:${userKey}`;
    const LEGACY_COMPLETED_KEY = 'neo-onboarding-completed';
    const LEGACY_NEW_SIGNUP_KEY = 'new-signup';

    localStorage.setItem(COMPLETED_KEY, 'true');
    localStorage.setItem(LEGACY_COMPLETED_KEY, 'true');
    localStorage.removeItem(NEW_SIGNUP_KEY);
    localStorage.removeItem(LEGACY_NEW_SIGNUP_KEY);

    const persisted = await persistOnboardingCompleted(contextUser, contextTenant);
    if (!persisted) {
      console.warn(
        'Onboarding: saved locally; server persist failed (check /api/settings/user supports onboarding_completed)',
      );
    }
  }, [contextUser, contextTenant]);

  const handleSkip = useCallback(async () => {
    await markComplete();
    setIsVisible(false);
    onComplete?.();
  }, [markComplete, onComplete]);

  const handleComplete = useCallback(async () => {
    await markComplete();
    setIsVisible(false);
    onComplete?.();
    toast({ title: "Welcome to NEO GTM!", description: "You're all set up!" });
  }, [markComplete, onComplete, toast]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleQuickAction = (action) => {
    onQuickAction?.(action);
    handleComplete();
  };

  /* 🔴 HOLD THE WHOLE TOUR UNTIL THE CHECKLIST RESOLVES. Measured in Chrome: without this the
   * welcome card paints with SIX progress dots and silently becomes SEVEN when the read lands —
   * the rep watches the length of the tour change while reading the first sentence. The earlier
   * version of this component claimed in a comment that it could not happen; it could, and did.
   * The wait is bounded above, so this can delay the tour but never prevent it. */
  if (!isVisible || !setupLoaded) return null;

  // Modal steps (first and last)
  if (currentStepData.type === 'modal') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <Card className="border-0 shadow-2xl bg-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <AnimatedNeoSymbol size={40} showOrbits={false} />
                  <div className="w-2 h-2 bg-[#00C2C2] rounded-full animate-pulse"></div>
                </div>
                <Button variant="ghost" size="icon" onClick={handleSkip} className="h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <h2 className="text-xl font-bold text-[#0A1A2F] mb-4">
                {currentStepData.title}
              </h2>
              
              <div className="text-[#5C6B7A] mb-6 space-y-2">
                {currentStepData.content.split('\n').map((line, i) => (
                  <p key={i} className="text-sm">{line}</p>
                ))}
              </div>

              {currentStepData.quickActions && (
                <div className="mb-6">
                  <p className="text-sm text-[#5C6B7A] mb-3">Try these quick actions:</p>
                  <div className="flex flex-wrap gap-2">
                    {currentStepData.quickActions.map((action) => (
                      <Button
                        key={action}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAction(action)}
                        className="text-xs h-8 px-3"
                      >
                        {action}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  {steps.map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i === currentStep ? 'bg-[#00C2C2]' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                
                <div className="flex gap-2">
                  {currentStepData.secondaryButton && (
                    <Button variant="outline" onClick={handleSkip} size="sm" className="text-xs h-8 px-3">
                      {currentStepData.secondaryButton}
                    </Button>
                  )}
                  <Button onClick={handleNext} size="sm" className="bg-[#00C2C2] hover:bg-[#00A3A3] text-xs h-8 px-3">
                    {currentStepData.primaryButton || 'Next'}
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  /* ── the setup checklist ────────────────────────────────────────────────────────────────────────
   *
   * Every row is a real gap on this rep's account, with the reason it matters rather than a bare
   * label — "Connect your mailbox" tells them nothing; "nothing can be sent until you do" tells
   * them why they are reading this.
   *
   * 🔴 THE MAILBOX IS A BLOCKER AND THE REST ARE QUALITY. It is pulled to the top and marked,
   * because without it NOTHING can be sent and a rep otherwise discovers that at the moment they
   * press send — which reads as a broken product rather than a step they have not done.
   *
   * ⚠️ LINKS OPEN IN A NEW TAB. Navigating away mid-tour would lose the tour, and a rep who
   * connects their mailbox should come back to where they were. */
  if (currentStepData.type === 'setup') {
    const blocker = missing.find((m) => m.key === 'mailbox');
    const rest = missing.filter((m) => m.key !== 'mailbox');
    const ordered = blocker ? [blocker, ...rest] : rest;
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg"
        >
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <AnimatedNeoSymbol size={40} showOrbits={false} />
                <div className="w-2 h-2 bg-[#00C2C2] rounded-full animate-pulse mt-2"></div>
              </div>

              {/* ⚠️ The count is real, so the copy has to agree with it. "A couple of things to
                  finish" above a list of one is the same small lie as "1 people", and it is the
                  kind a rep notices on the very first screen they are shown. */}
              <h3 className="text-lg font-bold text-[#0A1A2F] mb-2">
                {blocker
                  ? 'One thing before you can send'
                  : ordered.length === 1 ? 'One thing to finish' : 'A couple of things to finish'}
              </h3>
              <p className="text-sm text-[#5C6B7A] mb-5">
                {blocker
                  ? "NEO sends from your own mailbox, so replies come back to you — that has to be connected once before anything can go out."
                  : ordered.length === 1
                    ? 'This shapes how your messages read. You can do it now or later; NEO works either way.'
                    : 'These shape how your messages read. You can do them now or later; NEO works either way.'}
              </p>

              <ul className="space-y-3 mb-6">
                {ordered.map((item) => {
                  const isBlocker = item.key === 'mailbox';
                  return (
                    <li
                      key={item.key}
                      className={`flex items-start gap-3 rounded-lg border p-3 ${
                        isBlocker ? 'border-amber-200 bg-amber-50/60' : 'border-gray-200'
                      }`}
                    >
                      {isBlocker ? (
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      ) : (
                        <Circle className="w-4 h-4 text-gray-300 mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#0A1A2F]">
                          {SETUP_LABELS[item.key] || item.key.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-[#5C6B7A] mt-0.5">{item.why_it_matters}</p>
                      </div>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#00A3A3] hover:underline mt-0.5"
                        >
                          Set up
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>

              <div className="flex justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  {steps.map((_, i) => (
                    <div
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        i === currentStep ? 'bg-[#00C2C2]' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>

                <div className="flex gap-2">
                  {currentStep > 0 && (
                    <Button variant="outline" size="sm" onClick={handleBack}>
                      <ArrowLeft className="w-3 h-3 mr-1" />
                      Back
                    </Button>
                  )}
                  {/* A deferral, not a refusal — this comes back in a week. */}
                  <Button size="sm" onClick={handleNext} className="bg-[#00C2C2] hover:bg-[#00A3A3]">
                    {currentStep === steps.length - 1 ? 'Finish' : "I'll do this later"}
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                  {/* The only control that writes a permanent decline. */}
                  <Button variant="ghost" size="sm" onClick={handleDeclineSetup} className="text-xs">
                    Don&apos;t ask again
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Simple centered steps (middle steps)
  if (currentStepData.type === 'simple') {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <AnimatedNeoSymbol size={40} showOrbits={false} />
                <div className="w-2 h-2 bg-[#00C2C2] rounded-full animate-pulse mt-2"></div>
              </div>
              
              <h3 className="text-lg font-bold text-[#0A1A2F] mb-3">
                {currentStepData.title}
              </h3>
              
              <div className="text-sm text-[#5C6B7A] mb-6 space-y-2">
                {currentStepData.content.split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {steps.map((_, i) => (
                    <div
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        i === currentStep ? 'bg-[#00C2C2]' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                
                <div className="flex gap-2">
                  {currentStep > 0 && (
                    <Button variant="outline" size="sm" onClick={handleBack}>
                      <ArrowLeft className="w-3 h-3 mr-1" />
                      Back
                    </Button>
                  )}
                  <Button size="sm" onClick={handleNext} className="bg-[#00C2C2] hover:bg-[#00A3A3]">
                    {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleSkip} className="text-xs">
                    Skip
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return null;
}
